import React, { useContext } from 'react'
import {
  AccountCircleTwoTone,
  AssignmentTwoTone,
  BookmarksTwoTone,
  BusinessCenterTwoTone,
  CollectionsBookmarkTwoTone,
  ExitToAppTwoTone,
  FaceTwoTone,
  HdrStrongTwoTone,
  InfoTwoTone,
  KeyboardArrowRight,
  MailTwoTone,
  WebTwoTone,
  CardGiftcardTwoTone,
  SupervisedUserCircleTwoTone,
  AdminPanelSettingsTwoTone
} from '@mui/icons-material'
import { ActionButton } from '../../action-buttons'
import { routes } from '../../../routes'
import { Link } from 'react-router-dom'
import { useBonusMaterial, useExternalApp, useGroup } from '../../../hooks'
import makeStyles from '@mui/styles/makeStyles'
import MenuButton from '../menu-button'
import VerticalDivider from '../action-bar/divider'
import useAccessByContext from './useAccessByContext'
import { ALL_CONSUMER } from '../../../services/cognito/userGroups'
import { AppbarActionContext } from '../context'
import { Badge, Link as ExternalLink, SvgIcon } from '@mui/material'
import useFormFactor from './useFormFactor'
import { useDispatch, useSelector } from 'react-redux'
import { newsletterSlice } from '../../../reducers'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { SkillBitesIcon } from '../../../pages/skill-bites/components/skill-bites-icon'
import { useTenant } from '@emerald-works-nova/auth'
import consts from '../../../consts'

const useStyles = makeStyles(({ typography, palette, breakpoints }) => {
  const common = isConsumerRole => ({
    fontWeight: typography.weight.regular,
    ...(isConsumerRole && { color: palette.secondaryGrey.shade[80] }),
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem'
    }
  })

  const commonLink = isConsumerRole => ({
    ...common(isConsumerRole),
    padding: '6px 11px',
    '& .MuiButton-startIcon': {
      marginRight: 6
    }
  })

  return {
    link: ({ isConsumerRole }) => ({
      ...commonLink(isConsumerRole)
    }),
    menuButton: ({ isConsumerRole }) => ({
      ...commonLink(isConsumerRole),
      '&:hover': {
        textDecoration: 'none'
      }
    }),
    close: ({ isConsumerRole }) => ({
      ...common(isConsumerRole),
      backgroundColor: palette.common.white,
      boxShadow: 'none'
    }),
    signin: ({ bps }) => ({
      [breakpoints.up(bps.smallDesktop)]: {
        // ActionButton variant ghost renders too high by the size of the border.
        paddingTop: 7,
        paddingBottom: 7
      }
    })
  }
})

function NewBadge (props) {
  return (
    <SvgIcon {...props}>
      <path d='M7.12 10.47V5.53003H6.09V8.41003L4.03 5.53003H3V10.47H4.03V7.59003L6.13 10.47H7.12ZM11.24 6.57003V5.53003H7.94V10.47H11.24V9.44003H9.18V8.53003H11.24V7.49003H9.18V6.57003H11.24ZM12.06 5.53003V9.65003C12.06 10.1 12.43 10.47 12.88 10.47H16.17C16.62 10.47 16.99 10.1 16.99 9.65003V5.53003H15.96V9.24003H15.04V6.35003H14.01V9.25003H13.08V5.53003H12.06V5.53003Z' />
      <path opacity='0.3' d='M2 2H18V14H2V2Z' />
      <path d='M18 0H2C0.89 0 0.00999999 0.89 0.00999999 2L0 14C0 15.11 0.89 16 2 16H18C19.11 16 20 15.11 20 14V2C20 0.89 19.11 0 18 0ZM18 14H2V2H18V14Z' />
    </SvgIcon>
  )
}

const useActions = () => {
  const { isInGroup } = useGroup()
  const { breakpoints, formFactor } = useFormFactor()
  const { newestBonusMaterialCount } = useBonusMaterial()
  const tenant = useTenant()
  const isPopoverOpen = useSelector(newsletterSlice.selectors.selectIsNewsletterPopoverOpen)
  const [trackNewsletterOpenPopover] = useEvent([{ eventName: 'newsletterOpenPopover' }])
  const classes = useStyles({
    isConsumerRole: isInGroup(ALL_CONSUMER),
    bps: breakpoints
  })
  const { getMtUrl, arkUrl, reportingUrl } = useExternalApp()

  const isSmallDisplay = ['mobile', 'tablet'].includes(formFactor)
  const linkSize = isSmallDisplay ? 'xl' : 'm'
  const buttonSize = isSmallDisplay ? 'l' : 's'

  const handleUserManagementPage = () => {
    window.open(`${arkUrl}tenants/${tenant.pk}`)
  }

  const handleOpenReportingDashboard = () => {
    window.open(reportingUrl)
  }

  /* Actions need both an id (used to access check) and a key (needed by React).
   * The key alone can't be used as it's mutated at various points by React.
   *
   * Actions whose onClick can't be fully handled in here (e.g. those that set
   * modal state in other components) can use the AppbarActionContext's onClick
   * or onShowMenu functions. The onClick is fired for every action. E.g. See the
   * explore action handling in the app toolbar.
   */
  const allActions = [
    <ActionButton
      id='explore-action'
      key='explore-action'
      variant={isInGroup(ALL_CONSUMER) ? 'publicTenantSecondary' : 'primary'}
      fullWidth
      size='xl'
      endIcon={<KeyboardArrowRight />}
      label='Explore'
      data-test='appbar-expore-button'
    />,
    <ActionButton
      id='howitworks-action'
      key='howitworks-action'
      variant='link'
      component={ExternalLink}
      href={getMtUrl('howitworks')}
      size={linkSize}
      startIcon={<InfoTwoTone />}
      label='How it works'
      data-test='appbar-howitworks-button'
      className={classes.link}
    />,
    <ActionButton
      id='blog-action'
      key='blog-action'
      variant='link'
      component={ExternalLink}
      href={getMtUrl('blog')}
      size={linkSize}
      startIcon={<WebTwoTone />}
      label='Blog'
      data-test='appbar-blog-button'
      className={classes.link}
    />,
    <Badge
      key='bonusmaterial-action'
      id='bonusmaterial-action'
      badgeContent={newestBonusMaterialCount}
      color='primary'
      overlap='circular'
    >
      <ActionButton
        key='bonusmaterial-action'
        id='bonusmaterial-action'
        variant='link'
        component={Link}
        to={routes['bonus-material'].path}
        size={linkSize}
        startIcon={<CardGiftcardTwoTone />}
        label='Bonus'
        data-test='appbar-bonusmaterial-button'
        className={classes.link}
      />
    </Badge>,
    <ActionButton
      id='newsletter-action'
      key='newsletter-action'
      variant='link'
      onClick={() => {
        if (!isPopoverOpen) {
          trackNewsletterOpenPopover.trigger({
            from: 'appbar'
          })
        }
        dispatch(newsletterSlice.actions.toggleNewsletterPopover())
      }}
      size={linkSize}
      startIcon={<MailTwoTone />}
      label='Newsletter'
      data-test='appbar-newsletter-button'
      className={classes.link}
    />,
    <ActionButton
      id='forbusiness-action'
      key='forbusiness-action'
      variant='link'
      component={ExternalLink}
      href={getMtUrl('forbusiness')}
      size={linkSize}
      startIcon={<BusinessCenterTwoTone />}
      label='For Business'
      data-test='appbar-forbusiness-button'
      className={classes.link}
    />,
    <ActionButton
      key='skillbites-action'
      id='skillbites-action'
      variant='link'
      component={Link}
      to={routes['skill-bites'].path}
      size={linkSize}
      startIcon={<SkillBitesIcon />}
      label='Skill Bites'
      endIcon={<NewBadge style={{ height: 30, width: 30 }} viewBox='0 0 28 28' />}
      data-test='appbar-skillbites-button'
      className={classes.link}
    />,
    <ActionButton
      id='playlists-action'
      key='playlists-action'
      variant='link'
      component={Link}
      to={routes.playlists.path}
      size={linkSize}
      startIcon={<CollectionsBookmarkTwoTone />}
      label='Playlists'
      data-test='appbar-playlist-button'
      className={classes.link}
      popper={{
        enabled: true,
        title: 'Click here to access playlists',
        content: 'Create your own Playlist, by adding and organizing articles, resources and tools in one place.'
      }}
    />,
    <ActionButton
      id='reporting-action'
      key='reporting-action'
      variant='link'
      size={linkSize}
      startIcon={<AssignmentTwoTone />}
      label='Reporting'
      data-test='appbar-reporting-button'
      onClick={handleOpenReportingDashboard}
      className={classes.link}
    />,
    <ActionButton
      id='usermanagement-action'
      key='usermanagement-action'
      variant='link'
      size={linkSize}
      startIcon={<SupervisedUserCircleTwoTone />}
      label='User Management'
      data-test='user-management-button'
      onClick={handleUserManagementPage}
      className={classes.link}
    />,
    <ActionButton
      id='bookmarks-action'
      key='bookmarks-action'
      variant='link'
      component={Link}
      to={routes.bookmarks.path}
      size={linkSize}
      startIcon={<BookmarksTwoTone />}
      label='Bookmarks'
      data-test='appbar-bookmarks-button'
      className={classes.link}
    />,
    <ActionButton
      id='noauthbookmarks-action'
      key='noauthbookmarks-action'
      variant='link'
      size={linkSize}
      startIcon={<BookmarksTwoTone />}
      label='Bookmarks'
      data-test='appbar-bookmarks-button'
      className={classes.link}
    />,
    <MenuButton
      id='admintools-action'
      key='admintools-action'
      filterContext='adminTools'
      button={
        <ActionButton
          variant='link'
          size='m'
          startIcon={<AdminPanelSettingsTwoTone />}
          label='Admin Tools'
          data-test='appbar-admin-tools-button'
          className={classes.menuButton}
        />
      }
    />,
    <ActionButton
      id='profile-action'
      key='profile-action'
      variant='link'
      component={Link}
      to={routes.profile.path}
      size={linkSize}
      startIcon={<FaceTwoTone />}
      label='Me'
      data-test='appbar-profile-button'
      className={classes.link}
    />,
    <ActionButton
      id='logout-action'
      key='logout-action'
      variant='link'
      size={linkSize}
      startIcon={<ExitToAppTwoTone />}
      label='Logout'
      data-test='appbar-profile-button'
      className={classes.link}
    />,
    <ActionButton
      id='join-action'
      key='join-action'
      variant='join'
      component={ExternalLink}
      href={getMtUrl('join')}
      size={buttonSize}
      fullWidth
      label='Join Mind Tools'
      data-test='appbar-join-button'
      className={classes.join}
    />,
    <ActionButton
      id='signin-action'
      key='signin-action'
      variant='ghost'
      component={Link}
      to={routes.login.path}
      fullWidth
      size={buttonSize}
      startIcon={<AccountCircleTwoTone />}
      label='Sign In'
      data-test='appbar-profile-button'
      className={classes.signin}
    />,
    <MenuButton
      id='more-action'
      key='more-action'
      button={
        <ActionButton
          variant='link'
          size='m'
          startIcon={<HdrStrongTwoTone />}
          label='More'
          data-test='appbar-show-more-button'
          className={classes.menuButton}
        />
      }
    />,
    <MenuButton
      id='account-action'
      key='account-action'
      button={
        <ActionButton
          variant='link'
          size='m'
          startIcon={<AccountCircleTwoTone />}
          label='Account'
          data-test='appbar-account-button'
          className={classes.menuButton}
        />
      }
    />,
    <VerticalDivider id='divider' key='divider' />
  ].filter(({ props: { id } }) => {
    const isClub = tenant.pk === consts.DEFAULT_CLUB_PK
    if (!tenant) return false
    if (id === 'usermanagement-action') return tenant.toolkitType === consts.TOOLKIT_TYPE.smallBusinessToolkit
    if (id === 'skillbites-action') return tenant.features?.skillBites
    // Always ignore playlist feature flag for club
    if (id === 'playlists-action') return tenant.features?.playlists || isClub
    return true
  })

  const dispatch = useDispatch()
  const { getId } = useContext(AppbarActionContext)

  const { accessInContext } = useAccessByContext()

  const filterActions = context => {
    return allActions.filter(action => accessInContext(context, getId(action)))
  }

  return { filterActions }
}

export default useActions
